import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { useData } from "@/lib/useData";
import type { Company } from "@/lib/types";
import { Plus, MoreHorizontal, Pencil, Trash2, Search } from "lucide-react";

const emptyCompany = {
	name: "",
	industry: "",
	website: "",
	phone: "",
	address: "",
};

export function CompaniesPage() {
	const companiesFetcher = useCallback(() => api.companies.list(), []);
	const { data: companies, loading, refresh } = useData<Company[]>(companiesFetcher);

	const [search, setSearch] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<Company | null>(null);
	const [form, setForm] = useState(emptyCompany);

	const filtered = companies?.filter(c => {
		const q = search.toLowerCase();
		return c.name.toLowerCase().includes(q) || c.industry.toLowerCase().includes(q);
	}) ?? [];

	function openCreate() {
		setEditing(null);
		setForm(emptyCompany);
		setDialogOpen(true);
	}

	function openEdit(company: Company) {
		setEditing(company);
		setForm({
			name: company.name,
			industry: company.industry,
			website: company.website,
			phone: company.phone,
			address: company.address,
		});
		setDialogOpen(true);
	}

	async function handleSave() {
		if (editing) {
			await api.companies.update(editing.id, form);
		} else {
			await api.companies.create(form);
		}
		setDialogOpen(false);
		refresh();
	}

	async function handleDelete(id: string) {
		await api.companies.delete(id);
		refresh();
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Companies</h1>
					<p className="text-sm text-muted-foreground">{companies?.length ?? 0} total companies</p>
				</div>
				<Button onClick={openCreate} size="sm">
					<Plus className="h-4 w-4 mr-1" /> Add Company
				</Button>
			</div>

			<Card>
				<CardHeader className="pb-3">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder="Search companies..."
							value={search}
							onChange={e => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>
				</CardHeader>
				<CardContent className="p-0">
					{loading ? (
						<div className="p-6 text-center text-muted-foreground">Loading...</div>
					) : filtered.length === 0 ? (
						<div className="p-6 text-center text-muted-foreground">No companies found</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Industry</TableHead>
									<TableHead>Phone</TableHead>
									<TableHead>Website</TableHead>
									<TableHead>Address</TableHead>
									<TableHead className="w-10"></TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filtered.map(company => (
									<TableRow key={company.id}>
										<TableCell className="font-medium">{company.name}</TableCell>
										<TableCell>{company.industry}</TableCell>
										<TableCell className="text-muted-foreground">{company.phone}</TableCell>
										<TableCell className="text-muted-foreground">{company.website}</TableCell>
										<TableCell className="text-muted-foreground max-w-[200px] truncate">{company.address}</TableCell>
										<TableCell>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" size="icon" className="h-8 w-8">
														<MoreHorizontal className="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													<DropdownMenuItem onClick={() => openEdit(company)}>
														<Pencil className="h-4 w-4 mr-2" /> Edit
													</DropdownMenuItem>
													<DropdownMenuItem className="text-destructive" onClick={() => handleDelete(company.id)}>
														<Trash2 className="h-4 w-4 mr-2" /> Delete
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{editing ? "Edit Company" : "New Company"}</DialogTitle>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="space-y-2">
							<Label>Company Name</Label>
							<Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
						</div>
						<div className="space-y-2">
							<Label>Industry</Label>
							<Input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} />
						</div>
						<div className="space-y-2">
							<Label>Phone</Label>
							<Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
						</div>
						<div className="space-y-2">
							<Label>Website</Label>
							<Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
						</div>
						<div className="space-y-2">
							<Label>Address</Label>
							<Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
						<Button onClick={handleSave}>{editing ? "Save Changes" : "Create Company"}</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
